import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag } from '../../../../../components';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { BASE_URL } from '../../../../../utils';
import axios from 'axios';

const initialTubeDataObj = {
  data: [],
  loading: false
};

export const TubeTypeMaster = () => {
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [tubeDataObj, setTubeDataObj] = useState(initialTubeDataObj);
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [activeTab, setActiveTab] = useState(0);
  const [input, setInput] = useState('');

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        tube_code: Yup.string().required('Tube Code is Required'),
        description: Yup.string().required('Description is Required'),
        color: Yup.string().required('Color is Required'),
        additive: Yup.string().required('Additive is Required'),
        typical_volume_ml: Yup.number()
          .required('Typical Volume is Required')
          .min(0.1, 'Volume must be at least 0.1 ml')
          .typeError('Volume must be a number'),
        draw_order: Yup.number()
          .required('Draw Order is Required')
          .min(1, 'Draw order must be at least 1')
          .integer('Draw order must be a whole number')
          .typeError('Draw order must be a number')
      })
    ),
    defaultValues: {
      typical_volume_ml: 0,
      draw_order: 1,
      status: 'active'
    },
    mode: 'all'
  });

  const { handleSubmit, setValue, reset, watch } = methods;

  const statusValue = watch('status');

  useEffect(() => {
    if (selectedRecord) {
      console.log('SELECTED RECORD', selectedRecord);
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', '__v', 'createdAt', 'updatedAt']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
    } else {
      reset();
    }
  }, [selectedRecord, setValue, reset]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }

    const payload = {
      ...formValues,
      tube_code: formValues.tube_code.toUpperCase().trim()
    };

    const response = await request(editFlag ? '/tube-type-update' : '/tube-type-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: payload
    });

    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Tube Type`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Tube Type`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/tube-type-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Tube Type', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Tube Type', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setTubeDataObj({ loading: true });
    const response = await axios.get(`${BASE_URL}/tube-type/list?limit=${10}`);
    if (response?.data?.success) {
      setTubeDataObj({ loading: false, data: response.data?.data });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setTubeDataObj({ loading: false });
    }
  };

  const TABLE_COLUMNS = [
    { header: 'Tube Code', accessor: 'tube_code' },
    { header: 'Description', accessor: 'description' },
    { header: 'Color', accessor: 'color' },
    { header: 'Additive', accessor: 'additive' },
    { header: 'Volume (ml)', accessor: 'typical_volume_ml' },
    { header: 'Draw Order', accessor: 'draw_order' },
    { 
      header: 'Status', 
      accessor: 'status', 
      sx: { 
        textTransform: 'uppercase',
        color: (row) => 
          row.status === 'active' ? 'success.main' : 
          row.status === 'default' ? 'primary.main' : 
          'error.main'
      } 
    },
    { header: 'Created At', accessor: 'createdAt' }
  ];

  const handleActiveTab = (newTabIndex) => {
    setActiveTab(newTabIndex);
  };

  useEffect(() => {
    handleTubeSearch();
  }, [input]);

  const handleTubeSearch = async () => {
    const lowercasedInput = input.toLowerCase().trim();
    
    if (lowercasedInput === '') {
      try {
        const response = await request('/tube-type-list', {
          method: 'GET'
        });
        if (response.success && Array.isArray(response.data)) {
          setTubeDataObj({ loading: false, data: response.data });
        } else {
          toast('Error in fetching data', { variant: 'error' });
          setTubeDataObj({ loading: false, data: [] });
        }
      } catch (error) {
        console.error('Error during fetch:', error);
        toast('Error during fetch', { variant: 'error' });
        setTubeDataObj({ loading: false, data: [] });
      }
    } else {
      try {
        const response = await request('/tube-type-list', {
          method: 'GET',
          query: { search: lowercasedInput }
        });
        if (response.success && Array.isArray(response.data)) {
          setTubeDataObj({ loading: false, data: response.data });
        } else {
          toast('Error in fetching data', { variant: 'error' });
          setTubeDataObj({ loading: false, data: [] });
        }
      } catch (error) {
        console.error('Error during search:', error);
        toast('Error during search', { variant: 'error' });
        setTubeDataObj({ loading: false, data: [] });
      }
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const statusOptions = [
    { _id: 'active', name: 'Active' },
    { _id: 'default', name: 'Default' },
    { _id: 'inactive', name: 'Inactive' }
  ];

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Tube Type Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={handleSubmit(formSubmit)}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction)}
                  onClick={handleQueryWindow}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn id={'Exit'} variant="contained">
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>

            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Button
                variant="contained"
                onClick={() => handleActiveTab(0)}
                sx={{
                  my: 0,
                  mx: 1,
                  borderRadius: '4px',
                  fontWeight: 700
                }}
              >
                Main
              </Button>
              <Grid container spacing={4}>
                {activeTab === 0 && (
                  <>
                    <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: '1%' }}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Tube Code</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="tube_code"
                          placeholder="Enter Tube Code"
                          sx={{ flex: 0.7 }}
                          inputProps={{ style: { textTransform: 'uppercase' } }}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Description</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.7 }}
                          name="description"
                          placeholder="Enter Description"
                          multiline
                          rows={2}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Color</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.7 }}
                          name="color"
                          placeholder="Enter Color"
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: '1%' }}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Additive</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.7 }}
                          name="additive"
                          placeholder="Enter Additive"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Volume (ml)</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.7 }}
                          name="typical_volume_ml"
                          placeholder="Enter Typical Volume"
                          type="number"
                          step="0.1"
                          min="0.1"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Draw Order</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.7 }}
                          name="draw_order"
                          placeholder="Enter Draw Order"
                          type="number"
                          min="1"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Status</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.7 }}
                          name="status"
                          placeholder="Select Status"
                          menus={statusOptions}
                        />
                      </Stack>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Tube Type Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem'
              }
            }}
            label="Search Tube Type"
            variant="outlined"
            value={input}
            onChange={handleInputChange}
            InputProps={{
              style: {
                fontSize: '12px',
                fontWeight: 600
              }
            }}
          />
        </Stack>
        <Table
          sx={{ mt: 1 }}
          columns={TABLE_COLUMNS}
          data={tubeDataObj.data || []}
          loading={tubeDataObj.loading}
          onRowClick={(row) => {
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};